import { useCallback } from "react";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import {
  UploadItem,
  UploadItemStatus,
  UploadItemType,
  UploadSource,
} from "@/components/upload/types/upload-item";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { DownloadedFileWithIds } from "../providers/SharePointProvider";
import useAzureStorage from "./useAzureStorage";
import useWorkspace from "@/hooks/useWorkspace";
import { useMicrosoftDocxPdfStaging } from "./useMicrosoftDocxPdfStaging";
import { showInfoToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";

type UseFileUploadParams = {
  workspaceId: string | null;
  currentWorkspaceIdRef: React.MutableRefObject<string | null>;
  updateUploadsForWorkspace: (
    targetWorkspaceId: string,
    updater: (prev: UploadItem[]) => UploadItem[]
  ) => void;
};

type UseFileUploadReturn = {
  /**
   * Upload files from SharePoint/OneDrive with their IDs
   */
  handleUploadFiles: (
    filesWithIds: DownloadedFileWithIds[],
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  /**
   * Upload files from computer (FileList)
   */
  handleUploadFilesComputer: (
    files: FileList,
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  /**
   * Handle file input change event
   */
  onUploadFiles: (
    e: React.ChangeEvent<HTMLInputElement>,
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  /**
   * Create an upload item from a downloaded file
   */
  createUploadItem: (fileWithIds: DownloadedFileWithIds) => UploadItem;
};

/**
 * Creates an upload item from a downloaded file with SharePoint/OneDrive metadata
 */
const createUploadItem = (fileWithIds: DownloadedFileWithIds): UploadItem => {
  const uniqueBlobName = uuidv4();
  const fileExtension = fileWithIds.file.name.split(".").pop()?.toLowerCase();

  const type: UploadItemType =
    {
      pdf: UploadItemType.PDF,
      doc: UploadItemType.DOC,
      docx: UploadItemType.DOCX,
      xls: UploadItemType.XLS,
      xlsx: UploadItemType.XLSX,
      xlsm: UploadItemType.XLSM,
    }[fileExtension!] ?? UploadItemType.UNKNOWN;

  return {
    id: uniqueBlobName,
    name: fileWithIds.file.name,
    status: UploadItemStatus.Pending,
    type,
    source: fileWithIds.source,
    driveId: fileWithIds.driveId,
    siteId: fileWithIds.siteId,
    sharepointFileId: fileWithIds.sharepointFileId,
  };
};

/**
 * Hook to handle file upload operations
 */
export const useFileUpload = ({
  workspaceId,
  currentWorkspaceIdRef,
  updateUploadsForWorkspace,
}: UseFileUploadParams): UseFileUploadReturn => {
  const { uploadFileToAzure } = useAzureStorage();
  const { addDocuments } = useWorkspace();
  const { stageMicrosoftPdfIfNeeded } = useMicrosoftDocxPdfStaging();

  const handleUploadFiles = useCallback(
    async (
      filesWithIds: DownloadedFileWithIds[],
      workspaceFileNames: Record<string, string>,
      folderId?: string,
      metadata?: Record<string, WorkspaceDocumentMetadata[]>
    ) => {
      // Use current workspaceId from ref as fallback if closure value is stale
      const currentWorkspaceId = workspaceId || currentWorkspaceIdRef.current;
      if (!currentWorkspaceId) {
        logger.error("Upload aborted: no workspace id", {
          operation: "uploadFiles",
          fileCount: filesWithIds.length,
        });
        throw new Error("Workspace ID is required");
      }

      const tmpUploads = filesWithIds.map((file) => createUploadItem(file));
      updateUploadsForWorkspace(currentWorkspaceId, (prev) => [
        ...prev,
        ...tmpUploads,
      ]);

      // Upload DOCX/files to documents container, then stage Graph PDFs for cloud DOCX
      await Promise.all(
        tmpUploads.map((upload, index) => {
          const fileWithIds = filesWithIds[index];
          return (async () => {
            await uploadFileToAzure(
              fileWithIds.file,
              upload.id,
              currentWorkspaceId
            );
            await stageMicrosoftPdfIfNeeded(
              fileWithIds.file.name,
              upload.id,
              currentWorkspaceId,
              {
                driveId: fileWithIds.driveId,
                siteId: fileWithIds.siteId,
                sharepointFileId: fileWithIds.sharepointFileId,
                source: fileWithIds.source,
              }
            );
          })();
        })
      );

      // Create the documents
      const documents = tmpUploads.map((upload, index) => {
        const existingDocumentId =
          workspaceFileNames[filesWithIds[index].file.name.toLowerCase()];
        const fileMetadata = metadata?.[filesWithIds[index].file.name] || [];

        return {
          documentName: filesWithIds[index].file.name,
          documentBlobName: upload.id,
          documentSource: upload.source,
          driveId: upload.driveId,
          siteId: upload.siteId,
          sharepointFileId: upload.sharepointFileId,
          documentId: existingDocumentId || undefined, // undefined = new, value = revision
          documentMetadataList: fileMetadata,
          folderId,
        };
      });

      await addDocuments.mutateAsync({
        workspaceId: currentWorkspaceId,
        requestBody: {
          sessionId: currentWorkspaceId,
          documents,
        },
      });
    },
    [
      workspaceId,
      currentWorkspaceIdRef,
      updateUploadsForWorkspace,
      uploadFileToAzure,
      addDocuments,
      stageMicrosoftPdfIfNeeded,
    ]
  );

  const handleUploadFilesComputer = useCallback(
    async (
      files: FileList,
      workspaceFileNames: Record<string, string>,
      folderId?: string,
      metadata?: Record<string, WorkspaceDocumentMetadata[]>
    ) => {
      const filesArray = Array.from(files);
      const filesWithIds: DownloadedFileWithIds[] = filesArray.map((file) => ({
        file,
        driveId: undefined,
        siteId: undefined,
        sharepointFileId: undefined,
        source: UploadSource.LOCAL,
      }));

      if (filesArray.some((f) => f.name.toLowerCase().endsWith(".docx"))) {
        showInfoToast(
          "DOCX uploadet fra din computer bruger vores standardkonvertering. For bedre bevarelse af links kan du uploade via OneDrive/SharePoint i stedet.",
          "Tip til DOCX-upload"
        );
      }

      await handleUploadFiles(
        filesWithIds,
        workspaceFileNames,
        folderId,
        metadata
      );
    },
    [handleUploadFiles]
  );

  const onUploadFiles = useCallback(
    async (
      e: React.ChangeEvent<HTMLInputElement>,
      workspaceFileNames: Record<string, string>,
      folderId?: string,
      metadata?: Record<string, WorkspaceDocumentMetadata[]>
    ) => {
      const files = e.target.files;
      if (files) {
        await handleUploadFilesComputer(
          files,
          workspaceFileNames,
          folderId,
          metadata
        );
        // Reset the input value to allow selecting the same files again
        e.target.value = "";
      }
    },
    [handleUploadFilesComputer]
  );

  return {
    handleUploadFiles,
    handleUploadFilesComputer,
    onUploadFiles,
    createUploadItem,
  };
};
